import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Paper, Typography, Grid, CircularProgress,
  Button, ButtonGroup, TextField
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import PeopleAltIcon from "@material-ui/icons/PeopleAlt";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell
} from "recharts";
import moment from "moment";
import "moment/locale/pt-br";
import api from "../../services/api";
import { toast } from "react-toastify";

moment.locale("pt-br");

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    backgroundColor: theme.palette.background.default,
    minHeight: "100vh",
  },
  title: {
    fontWeight: 700,
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(3),
  },
  filterBar: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(3),
  },
  bigCard: {
    padding: theme.spacing(4),
    borderRadius: 16,
    textAlign: "center",
    background: "linear-gradient(135deg, #1a6b3c 0%, #27ae60 100%)",
    color: "#fff",
    boxShadow: "0 4px 20px rgba(39,174,96,0.3)",
  },
  bigNumber: {
    fontSize: "5rem",
    fontWeight: 900,
    lineHeight: 1,
    marginBottom: theme.spacing(1),
  },
  bigLabel: {
    fontSize: "1.1rem",
    opacity: 0.9,
    fontWeight: 500,
  },
  chartCard: {
    padding: theme.spacing(3),
    borderRadius: 16,
    height: "100%",
  },
  sectionTitle: {
    fontWeight: 700,
    fontSize: "1rem",
    marginBottom: theme.spacing(2),
    color: "#333",
  },
  agentRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 14px",
    borderRadius: 10,
    marginBottom: theme.spacing(1),
    background: theme.palette.background.default,
    border: "1px solid #e0e0e0",
  },
  agentName: {
    fontWeight: 600,
    fontSize: "0.9rem",
  },
  agentBadge: {
    background: "#27ae60",
    color: "#fff",
    borderRadius: 20,
    padding: "2px 14px",
    fontWeight: 700,
    fontSize: "0.9rem",
  },
  periodBtn: {
    textTransform: "none",
    fontWeight: 600,
  },
  dateInput: {
    "& input": { padding: "8px 10px", fontSize: "0.85rem" },
  },
  loading: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: 200,
  },
}));

const PERIODS = [
  { label: "Hoje", key: "today" },
  { label: "Ontem", key: "yesterday" },
  { label: "7 dias", key: "7d" },
  { label: "30 dias", key: "30d" },
  { label: "Período", key: "custom" },
];

const CHART_COLOR = "#27ae60";

const getDates = (period, customFrom, customTo) => {
  const today = moment().format("YYYY-MM-DD");
  switch (period) {
    case "today":
      return { from: today, to: today };
    case "yesterday": {
      const y = moment().subtract(1, "day").format("YYYY-MM-DD");
      return { from: y, to: y };
    }
    case "7d":
      return { from: moment().subtract(6, "days").format("YYYY-MM-DD"), to: today };
    case "30d":
      return { from: moment().subtract(29, "days").format("YYYY-MM-DD"), to: today };
    case "custom":
      return { from: customFrom || today, to: customTo || today };
    default:
      return { from: today, to: today };
  }
};

const formatDayLabel = (dia) => {
  return moment(dia).format("DD/MM");
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <Box style={{ background: "#fff", border: "1px solid #e0e0e0", borderRadius: 8, padding: "8px 14px" }}>
        <Typography style={{ fontWeight: 700, fontSize: "0.85rem" }}>{moment(label).format("ddd DD/MM")}</Typography>
        <Typography style={{ color: CHART_COLOR, fontWeight: 700 }}>{payload[0].value} leads</Typography>
      </Box>
    );
  }
  return null;
};

const ControleLeads = () => {
  const classes = useStyles();
  const [period, setPeriod] = useState("today");
  const [customFrom, setCustomFrom] = useState(moment().format("YYYY-MM-DD"));
  const [customTo, setCustomTo] = useState(moment().format("YYYY-MM-DD"));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const { from, to } = getDates(period, customFrom, customTo);
      const res = await api.get("/leads-control", {
        params: { dateFrom: from, dateTo: to },
      });
      setData(res.data);
    } catch (err) {
      toast.error("Erro ao carregar dados de leads");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [period, customFrom, customTo]);

  useEffect(() => {
    if (period !== "custom") {
      fetchLeads();
    }
  }, [period]);

  const handleApplyCustom = () => fetchLeads();

  const periodLabel = () => {
    const { from, to } = getDates(period, customFrom, customTo);
    if (from === to) return moment(from).format("DD/MM/YYYY");
    return `${moment(from).format("DD/MM")} – ${moment(to).format("DD/MM/YYYY")}`;
  };

  return (
    <Box className={classes.root}>
      <Typography variant="h5" className={classes.title}>
        Controle de Leads
      </Typography>

      {/* Filtros */}
      <Box className={classes.filterBar}>
        <ButtonGroup variant="outlined" size="small">
          {PERIODS.map((p) => (
            <Button
              key={p.key}
              className={classes.periodBtn}
              variant={period === p.key ? "contained" : "outlined"}
              color={period === p.key ? "primary" : "default"}
              onClick={() => setPeriod(p.key)}
            >
              {p.label}
            </Button>
          ))}
        </ButtonGroup>
        {period === "custom" && (
          <>
            <TextField
              type="date"
              size="small"
              variant="outlined"
              value={customFrom}
              onChange={(e) => setCustomFrom(e.target.value)}
              className={classes.dateInput}
              inputProps={{ max: customTo }}
            />
            <Typography style={{ color: "#666" }}>até</Typography>
            <TextField
              type="date"
              size="small"
              variant="outlined"
              value={customTo}
              onChange={(e) => setCustomTo(e.target.value)}
              className={classes.dateInput}
              inputProps={{ min: customFrom }}
            />
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={handleApplyCustom}
              style={{ textTransform: "none", fontWeight: 600 }}
            >
              Aplicar
            </Button>
          </>
        )}
      </Box>

      {loading ? (
        <Box className={classes.loading}>
          <CircularProgress style={{ color: "#27ae60" }} />
        </Box>
      ) : (
        <Grid container spacing={3}>
          {/* Card número principal */}
          <Grid item xs={12} md={3}>
            <Paper className={classes.bigCard}>
              <PeopleAltIcon style={{ fontSize: "2.5rem", opacity: 0.85, marginBottom: 8 }} />
              <Typography className={classes.bigNumber}>
                {data?.total ?? "—"}
              </Typography>
              <Typography className={classes.bigLabel}>
                Leads novos
              </Typography>
              <Typography style={{ fontSize: "0.8rem", opacity: 0.75, marginTop: 6 }}>
                {periodLabel()}
              </Typography>
            </Paper>
          </Grid>

          {/* Gráfico */}
          <Grid item xs={12} md={9}>
            <Paper className={classes.chartCard}>
              <Typography className={classes.sectionTitle}>
                Leads por dia
              </Typography>
              {data?.porDia && data.porDia.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart
                    data={data.porDia}
                    margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis
                      dataKey="dia"
                      tickFormatter={formatDayLabel}
                      tick={{ fontSize: 12 }}
                    />
                    <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="total" radius={[6, 6, 0, 0]}>
                      {data.porDia.map((entry, index) => (
                        <Cell key={index} fill={CHART_COLOR} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Box style={{ textAlign: "center", padding: 40, color: "#aaa" }}>
                  <Typography>Nenhum lead no período selecionado</Typography>
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Leads por atendente */}
          <Grid item xs={12}>
            <Paper className={classes.chartCard}>
              <Typography className={classes.sectionTitle}>
                Leads por atendente
              </Typography>
              {data?.porAtendente && data.porAtendente.length > 0 ? (
                <Grid container spacing={2}>
                  {data.porAtendente.map((ag, i) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
                      <Box className={classes.agentRow}>
                        <Box>
                          <Typography className={classes.agentName}>
                            {ag.nome}
                          </Typography>
                          <Typography style={{ fontSize: "0.75rem", color: "#888" }}>
                            {ag.userId ? `ID ${ag.userId}` : "Aguardando aceite"}
                          </Typography>
                        </Box>
                        <Box className={classes.agentBadge}>{ag.total}</Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Typography style={{ color: "#aaa", textAlign: "center", padding: 20 }}>
                  Nenhum dado disponível
                </Typography>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default ControleLeads;
